/**
 * Alerts funnel analytics — the one place the alerts UI talks to
 * `trackAnalyticsEvent`, so the event names and the property set stay small,
 * explicit and reviewable (the backend allow-lists both, see
 * profiteia-backend `analytics/taxonomy.py`).
 *
 * Privacy contract: NO PII and NO alert content. Never the alert name, a
 * threshold / operator / value, an email address, a webhook or a search term.
 * What does travel: where the user came from (`entry_point`, one closed list —
 * `ALERTS_ENTRY_POINTS` — enforced in `track()`), which creation
 * flow (`flow`), the condition TYPE (`alert_type`), a catalog template id
 * (`template_id`; a user's own template is reported as `user`), coded reasons,
 * and the dataset id in the top-level `dataset_id` field.
 *
 * Failure contract: nothing in here throws. A broken analytics transport can
 * never turn a successful save into an error.
 */
import { trackAnalyticsEvent } from '@/composables/useAnalytics'

export const ALERTS_EVENTS = Object.freeze({
  PAGE_VIEWED: 'alerts.page_viewed',
  BUILDER_OPENED: 'alerts.builder_opened',
  TEMPLATE_PICKED: 'alerts.template_picked',
  VALIDATION_SHOWN: 'alerts.validation_shown',
  CREATED: 'alerts.created',
  TEST_NOTIFY_CLICKED: 'alerts.test_notify_clicked',
  UPGRADE_CLICKED: 'alerts.upgrade_clicked',
  TEASER_SHOWN: 'alerts.teaser_shown'
})

/** Creation flows: the context-driven builder and the step wizard. */
export const ALERTS_FLOW = Object.freeze({ BUILDER: 'builder', WIZARD: 'wizard' })

/**
 * THE list of `entry_point` values; `track()` drops anything else. It covers
 * where a creation flow was entered from (CTAs pass it as `?entry=<value>`; a
 * bookmark, a typed URL or an unknown value reads `direct`), where the locked
 * teaser was shown, and where an upgrade or template click happened.
 */
export const ALERTS_ENTRY_POINTS = Object.freeze([
  'alerts_page',
  'dataset_page',
  'dataset_browser',
  'my_environment_card',
  'my_environment_menu',
  'suggested_template',
  'duplicate',
  'direct',
  'home_card',
  'condition_picker',
  'template_picker'
])

/** The only property keys an alerts event may carry. */
export const ALERTS_PROPERTY_KEYS = Object.freeze([
  'entry_point',
  'flow',
  'alert_type',
  'template_id',
  'step',
  'reason',
  'status_code',
  'result_count'
])

export function entryPointFromQuery (query) {
  const raw = Array.isArray(query?.entry) ? query.entry[0] : query?.entry
  return ALERTS_ENTRY_POINTS.includes(raw) ? raw : 'direct'
}

/**
 * For an intermediate step (the dataset browser in alert mode): forward the
 * whitelisted origin it was opened with, else name itself.
 */
export function forwardedEntryPoint (query, own) {
  const incoming = entryPointFromQuery(query)
  return incoming === 'direct' ? own : incoming
}

/** A catalog template's id is safe to report; a user's own template is not named. */
export function templateIdFor (template) {
  if (!template) return null
  return template.is_system === false || template.scope === 'user' || template.user_id
    ? 'user'
    : String(template.id || '')
}

/**
 * Coded reason for a save the API did not accept: a 4xx is the server
 * rejecting the alert, anything else (5xx, network, timeout) a failed request.
 */
export function saveFailureReason (status) {
  return status >= 400 && status < 500 ? 'server_rejected' : 'request_failed'
}

// Analytics must never affect the page: `trackAnalyticsEvent` touches
// sessionStorage / localStorage, which can throw (blocked site data, private
// mode). A throw from inside a save handler would read as "save failed" and
// invite a duplicate alert, so nothing leaves this function.
function track (eventName, properties = {}, { datasetId = null } = {}) {
  try {
    const clean = {}
    for (const key of ALERTS_PROPERTY_KEYS) {
      const value = properties[key]
      if (value === undefined || value === null || value === '') continue
      if (key === 'entry_point' && !ALERTS_ENTRY_POINTS.includes(value)) continue
      clean[key] = value
    }
    trackAnalyticsEvent(eventName, {
      resource_type: 'alert',
      ...(datasetId ? { dataset_id: datasetId } : {}),
      properties: clean
    })
  } catch {
    // best effort
  }
}

export const alertsAnalytics = {
  pageViewed: ({ resultCount, reason } = {}) =>
    track(ALERTS_EVENTS.PAGE_VIEWED, { result_count: resultCount, reason }),
  builderOpened: ({ flow, entryPoint, datasetId, templateId } = {}) =>
    track(ALERTS_EVENTS.BUILDER_OPENED, { flow, entry_point: entryPoint, template_id: templateId }, { datasetId }),
  templatePicked: ({ templateId, entryPoint, flow } = {}) =>
    track(ALERTS_EVENTS.TEMPLATE_PICKED, { template_id: templateId, entry_point: entryPoint, flow }),
  validationShown: ({ flow, step, reason, statusCode, alertType } = {}) =>
    track(ALERTS_EVENTS.VALIDATION_SHOWN, { flow, step, reason, status_code: statusCode, alert_type: alertType }),
  created: ({ flow, entryPoint, alertType, templateId, datasetId } = {}) =>
    track(
      ALERTS_EVENTS.CREATED,
      { flow, entry_point: entryPoint, alert_type: alertType, template_id: templateId },
      { datasetId }
    ),
  testNotifyClicked: ({ alertType } = {}) =>
    track(ALERTS_EVENTS.TEST_NOTIFY_CLICKED, { alert_type: alertType }),
  upgradeClicked: ({ entryPoint, reason, alertType } = {}) =>
    track(ALERTS_EVENTS.UPGRADE_CLICKED, { entry_point: entryPoint, reason, alert_type: alertType }),
  teaserShown: ({ entryPoint } = {}) =>
    track(ALERTS_EVENTS.TEASER_SHOWN, { entry_point: entryPoint })
}
